// Import necessary React hooks and libraries
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import type { User, Block } from '../types';

// ManageStudentsPage component: allows a teacher to view and manage students in a course
function ManageStudentsPage() {
  // Get the current user and loading state from authentication hook
  const { user, isLoading } = useAuth();
  // Get the course_id parameter from the URL
  const { course_id } = useParams<{ course_id: string }>();
  // State for students in the course
  const [students, setStudents] = useState<User[]>([]);
  // State for block information for students
  const [blocks, setBlocks] = useState<Block[]>([]);
  // State for displaying errors
  const [error, setError] = useState<string | null>(null);
  // Loading state for fetching students
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);
  // React Router navigate function
  const navigate = useNavigate();
  // Function to fetch students and their block status from the backend
  const fetchStudents = async () => {
    setIsLoadingStudents(true); // Set loading state
    try {
      // Fetch students for the current course
      const response = await axios.get(`/elearning/manage_students/${course_id}/`, {
        withCredentials: true, // Include credentials for authentication
      });
      // Handle both old and new response formats
      if (Array.isArray(response.data)) {
        // Old format: array of students with is_blocked property
        setStudents(response.data.map(student => ({
          id: student.id,
          username: student.username,
          real_name: student.real_name,
          role: student.role,
          photo: student.photo,
          email: student.email || null,
        })));
        // Set blocks for students who are blocked
        setBlocks(response.data
          .filter(student => student.is_blocked)
          .map(student => ({
            id: student.id, // Placeholder
            student: {
              id: student.id,
              username: student.username,
              real_name: student.real_name,
              role: student.role,
              photo: student.photo,
              email: student.email || null,
            },
            course: {
              id: Number(course_id),
              name: '', // Placeholder
              description: '', // Placeholder
              teacher: user!, // Current teacher
              slug: '', // Placeholder
              enrolled: false,
              materials: null,
            },
            is_blocked: student.is_blocked,
            blocked_by: user!, // Current teacher
            blocked_at: new Date().toISOString(),
          })));
      } else {
        // New format: response contains { students, blocks }
        setStudents(response.data.students || []);
        setBlocks(response.data.blocks || []);
      }
      setError(null); // Clear any previous errors
    } catch (err: any) {
      // Handle errors during fetch
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch students';
      console.error('ManageStudentsPage: Error fetching students=', errorMessage, 'status=', err.response?.status);
      setError(errorMessage);
    } finally {
      setIsLoadingStudents(false); // Reset loading state
    }
  };
  // useEffect: run on mount and when user, isLoading, course_id, or navigate changes
  useEffect(() => {
    if (isLoading) return; // Wait until auth state is loaded
    if (!user || user.role !== 'teacher') {
      // Redirect non-teachers to login
      navigate('/login');
      return;
    }
    // Fetch students after authentication check
    fetchStudents();
  }, [user, isLoading, course_id, navigate]);
  // Function to toggle block/unblock status for a student
  const handleBlockToggle = async (studentId: number, isBlocked: boolean) => {
    try {
      // Fetch CSRF token for POST request
      const response = await axios.get('/elearning/get-csrf/', { withCredentials: true });
      const csrfToken = response.data.csrfToken;
      // Send request to toggle block status
      await axios.post(
        '/elearning/api/blocks/',
        { student_id: studentId, course_id: course_id, is_blocked: !isBlocked },
        {
          withCredentials: true,
          headers: { 'X-CSRFToken': csrfToken, 'Content-Type': 'application/json' },
        }
      );
      // Refetch students to keep UI in sync
      await fetchStudents();
      setError(null);
    } catch (err: any) {
      // Handle errors while toggling block
      const errorMessage = err.response?.data?.message || err.message || 'Failed to toggle block status';
      setError(errorMessage);
      await fetchStudents(); // Refetch to ensure UI consistency
    }
  };
  // Render loading state if auth is still loading or user not available
  if (isLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-600"></div>
      </div>
    );
  }
  // Render message if user is not a teacher
  if (user.role !== 'teacher') {
    return (
      <div className="container mx-auto p-4 text-gray-900">
        Please log in as a teacher to manage students.
      </div>
    );
  }
  // Main page content
  return (
  <div className="min-h-screen bg-gray-50 flex justify-center py-12 px-4 sm:px-6 lg:px-8">
    <div className="w-full max-w-2xl">
      {/* Header with title and back link */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Manage Students</h1>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 bg-gray-200 text-gray-900 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Back to Homepage
        </button>
      </div>

      {/* Display error */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg shadow-sm">
          {error}
        </div>
      )}

      {/* Loading spinner */}
      {isLoadingStudents ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-blue-600"></div>
        </div>
      ) : students.length === 0 ? (
        <div className="bg-white p-6 rounded-2xl shadow-md text-center">
          <p className="text-gray-500 text-lg">No students enrolled in this course.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {students.map((student) => {
            const block = blocks
              .filter((b) => b.student.id === student.id)
              .sort((a, b) => new Date(b.blocked_at).getTime() - new Date(a.blocked_at).getTime())[0];
            const isBlocked = block ? block.is_blocked : false;

            return (
              <div
                key={student.id}
                className="bg-white p-5 rounded-2xl shadow-md hover:shadow-lg transition-shadow"
              >
                <p className="text-gray-800 font-medium mb-1">{student.username}</p>
                <p className="text-gray-700 mb-2">{student.real_name || 'No name provided'}</p>
                <p className="text-gray-700 mb-3">Status: {isBlocked ? 'Blocked' : 'Active'}</p>
                <button
                  onClick={() => handleBlockToggle(student.id, isBlocked)}
                  className={`w-full py-2 rounded-lg text-white transition-colors ${
                    isBlocked
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {isBlocked ? 'Unblock Student' : 'Block Student'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  </div>
);

}
// Export the ManageStudentsPage component as default
export default ManageStudentsPage;